import { useState } from "react";
import { useSession } from "../auth/session.js";
import { EmptyState, HonestState, Tag } from "../components.js";
import { Button } from "../components/controls.js";
import {
  canRunScenarioLevers,
  liveScenarioService,
  type Metric,
  type ScenarioAssumptionView,
  type ScenarioService,
  type ScenarioView,
  type ThroughputForecastValue,
} from "../api/scenarioService.js";
import {
  AssumptionBasisTag,
  leversForMode,
  type DeliveryMode,
  type LeverDef,
} from "./WhatIfLevers.js";
import { ForecastDeltaRibbon } from "./ForecastDeltaRibbon.js";
import { DateScopeComparison } from "./DateScopeComparison.js";
import {
  DEGRADED_NARRATIVE_SERVICE,
  NarrativeSeam,
  type NarrativeService,
} from "./NarrativeSeam.js";

/**
 * NX-2633 (E8.8) — the improvement simulator: a Project-detail sub-panel (design/08 §1 IA + §2 flow
 * 2), not a top-level nav destination. See this story's report for the corrected ticket framing:
 * two of its three cited blockers had already shipped (E7.5's scenario POST, E5.11's inverse
 * forecast function) by the time this was implemented; the third (E6.4's narrative module) exists
 * but has no HTTP route yet (that is NX-2639, a parallel story — see NarrativeSeam.tsx).
 *
 * AC5/SOLID: this is the ONLY component in the tree that calls the injected `ScenarioService` — the
 * lever definitions (WhatIfLevers.tsx) are a pure per-mode strategy map, the ribbon
 * (ForecastDeltaRibbon.tsx) takes baseline/scenario as props, and the narrative sub-panel
 * (NarrativeSeam.tsx) takes its already-resolved state as a prop. No sibling component does its own
 * inline fetch.
 *
 * AC7: `maySimulate` (client gate) hides lever controls entirely outside `SCENARIO_LEVER_ROLES`.
 * Read-only roles see the caller's baseline ribbon for scope-driven/continuous projects. The
 * caller's delivery-date forecast is not a date-driven scope-at-date forecast, so date-driven
 * projects show an honest gap until a scenario can run. No second request or disabled lever is
 * exposed. See scenarioService.ts's comment for the server-side gap this does not close.
 */

export interface WhatIfSimulatorPanelProps {
  readonly projectId: string;
  readonly deliveryMode: DeliveryMode | null;
  /** GET /projects/:id's own delivery-date forecast, reused for scope-driven/continuous ribbons.
   * Date-driven scope-at-date needs a scenario run, not this incompatible baseline. */
  readonly baselineForecast: Metric<ThroughputForecastValue>;
  readonly targetDate?: string | null;
  readonly service?: ScenarioService;
  readonly narrativeService?: NarrativeService;
}

type RunState =
  | { readonly status: "idle" }
  | { readonly status: "loading"; readonly leverId: string }
  | { readonly status: "error"; readonly leverId: string; readonly message: string }
  | { readonly status: "ready"; readonly leverId: string; readonly scenario: ScenarioView };

export function WhatIfSimulatorPanel({
  projectId,
  deliveryMode,
  baselineForecast,
  targetDate,
  service = liveScenarioService,
  narrativeService = DEGRADED_NARRATIVE_SERVICE,
}: WhatIfSimulatorPanelProps): React.ReactNode {
  const { session } = useSession();
  const [run, setRun] = useState<RunState>({ status: "idle" });
  const [tradeDate, setTradeDate] = useState("");
  const [tradeScope, setTradeScope] = useState("");
  const maySimulate = session !== null && canRunScenarioLevers(session.role);
  const levers = leversForMode(deliveryMode);
  const wiredLevers = levers.filter((l) => l.leverIds.length > 0);
  const gappedLever = levers.find((l) => l.leverIds.length === 0);

  const assumptions: readonly ScenarioAssumptionView[] =
    run.status === "ready" ? (run.scenario.assumptions ?? []) : [];
  const narrative = narrativeService.useNarrative(projectId, assumptions);

  const onEngage = (lever: LeverDef): void => {
    if (lever.leverIds.length === 0) return; // AC2/AC3 — a verified gap never fires a fabricated POST
    setRun({ status: "loading", leverId: lever.id });
    const selectedIds = tradeScope
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    const response =
      deliveryMode === "date-driven"
        ? service.run(projectId, lever.leverIds, session?.token ?? null, {
            ...(selectedIds.length > 0 ? { scopeItemIds: selectedIds } : {}),
            ...(tradeDate ? { targetDate: tradeDate } : {}),
          })
        : service.run(projectId, lever.leverIds, session?.token ?? null);
    response
      .then((envelope) => setRun({ status: "ready", leverId: lever.id, scenario: envelope.data }))
      .catch((err: unknown) =>
        setRun({
          status: "error",
          leverId: lever.id,
          message: err instanceof Error ? err.message : String(err),
        }),
      );
  };

  const baselineForRibbon: Metric<ThroughputForecastValue> =
    run.status === "ready" && run.scenario.mode !== "date-driven"
      ? run.scenario.baseline
      : baselineForecast;
  const scenarioForRibbon: Metric<ThroughputForecastValue> =
    run.status === "ready" && run.scenario.mode !== "date-driven"
      ? run.scenario.scenario
      : baselineForecast;

  return (
    <div className="panel pad">
      <div className="sectitle">
        <h3 className="sec">What-if simulator</h3>
        <span className="cnt">mode-aware levers · live forecast delta · narrative</span>
      </div>

      {deliveryMode === null ? (
        <HonestState
          kind="notobs"
          detail="No delivery mode is declared for this project (profile.deliveryMode is N/A), so the simulator cannot pick a mode-appropriate lever set."
        />
      ) : (
        <>
          {deliveryMode !== "date-driven" && (
            <ForecastDeltaRibbon baseline={baselineForRibbon} scenario={scenarioForRibbon} />
          )}
          {deliveryMode === "date-driven" && !maySimulate && (
            <HonestState
              kind="notobs"
              detail="A scope-at-date forecast requires a scenario run, which your role cannot start. The project overview still shows the read-only delivery-date forecast."
            />
          )}
          {run.status === "ready" && run.scenario.mode === "date-driven" && (
            <DateScopeComparison result={run.scenario} />
          )}

          {maySimulate ? (
            <div className="whatif open">
              {deliveryMode === "date-driven" && (
                <div className="lever">
                  <label>
                    Trade target date
                    <input
                      type="date"
                      value={tradeDate}
                      onChange={(event) => setTradeDate(event.target.value)}
                    />
                  </label>
                  <span>Declared target: {targetDate ?? "not declared"}</span>
                  <label>
                    Selected scope item IDs (comma-separated; blank drops last observed item)
                    <input
                      type="text"
                      value={tradeScope}
                      onChange={(event) => setTradeScope(event.target.value)}
                    />
                  </label>
                </div>
              )}
              {levers.map((lever) => (
                <div className="lever" key={lever.id}>
                  <div>
                    <div className="t">{lever.label}</div>
                    <div className="s">{lever.description}</div>
                  </div>
                  {lever.leverIds.length === 0 ? (
                    <Tag tone="amber">not yet wired</Tag>
                  ) : (
                    <Button
                      size="sm"
                      disabled={run.status === "loading"}
                      onClick={() => onEngage(lever)}
                    >
                      {run.status === "loading" && run.leverId === lever.id ? "Running…" : "Run"}
                    </Button>
                  )}
                </div>
              ))}

              {gappedLever !== undefined && (
                <HonestState
                  kind="notobs"
                  detail={gappedLever.gapReason ?? "This mode's lever has no live endpoint yet."}
                />
              )}

              {run.status === "error" && (
                <EmptyState message={`Couldn't run this scenario: ${run.message}`} />
              )}

              {run.status === "ready" && run.scenario.assumptions !== undefined && (
                <div className="atrisk-list">
                  {run.scenario.assumptions.map((a) => (
                    <div className="ar" key={a.leverId}>
                      <AssumptionBasisTag basis={a.basis} />
                      <span>{a.rationale}</span>
                    </div>
                  ))}
                </div>
              )}

              {wiredLevers.length > 0 && <NarrativeSeam state={narrative} />}
            </div>
          ) : (
            <EmptyState message="You are signed in, but your role can only view this project's forecast — running a what-if scenario is limited to delivery-side roles." />
          )}
        </>
      )}
    </div>
  );
}
